// src/components/theme/elements/ThemeSpeedLimiterIndicator.js

import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Group, Line } from 'react-konva';
import { toast } from 'react-toastify';
import CustomColorPicker from '../CustomColorPicker';
import styles from './styles.module.css';
import { clamp, snapToGridFn, boundDrag } from '../CanvasTools';
import { SCREEN_HEIGHT, SCREEN_WIDTH } from '../ScreenProps';

/**
 * Embedded Settings Component for Speed Limiter Indicator
 */
export const ThemeSpeedLimiterIndicatorSettings = ({ element, updateElement, openColorModal }) => {
    // Handler for size change
    const handleSizeChange = (e) => {
        const size = parseInt(e.target.value, 10);
        if (!isNaN(size) && size > 0) {
            updateElement(element.id, 'size', size);
        }
    };

    // Handler for corner orientation change
    const handleCornerChange = (e) => {
        updateElement(element.id, 'corner', e.target.value);
    };

    // Handlers for X and Y position changes
    const handleXChange = (e) => {
        const newX = parseInt(e.target.value, 10);
        if (!isNaN(newX)) {
            updateElement(element.id, 'x', newX);
        }
    };

    const handleYChange = (e) => {
        const newY = parseInt(e.target.value, 10);
        if (!isNaN(newY)) {
            updateElement(element.id, 'y', newY);
        }
    };

    // Corner orientation options
    const cornerOptions = [
        { value: '0', label: 'Bottom Left' },
        { value: '1', label: 'Bottom Right' },
        { value: '2', label: 'Top Left' },
        { value: '3', label: 'Top Right' },
    ];

    return (
        <div>
            {/* X Position */}
            <label>
                X Position:
                <input
                    className={styles.inputNumber}
                    type="number"
                    min="0"
                    max={SCREEN_WIDTH - 1}
                    value={element.x}
                    onChange={handleXChange}
                    style={{ marginLeft: '10px', width: '60px' }}
                    aria-label="Set X Position"
                />
            </label>

            {/* Y Position */}
            <label>
                Y Position:
                <input
                    className={styles.inputNumber}
                    type="number"
                    min="0"
                    max={SCREEN_HEIGHT - 1}
                    value={element.y}
                    onChange={handleYChange}
                    style={{ marginLeft: '10px', width: '60px' }}
                    aria-label="Set Y Position"
                />
            </label>

            {/* Size Control */}
            <label style={{ display: 'block', marginTop: '10px' }}>
                Size:
                <input
                    className={styles.inputNumber}
                    type="number"
                    value={element.size}
                    onChange={handleSizeChange}
                    min="10"
                    max="100"
                    step="1"
                    style={{ marginLeft: '10px', width: '60px' }}
                    aria-label="Set Size"
                />
            </label>

            {/* Corner Orientation */}
            <label style={{ display: 'block', marginTop: '10px' }}>
                Corner:
                <select
                    className={styles.select200}
                    value={element.corner}
                    onChange={handleCornerChange}
                    style={{ marginLeft: '10px', width: '120px' }}
                    aria-label="Set Corner Orientation"
                >
                    {cornerOptions.map((option) => (
                        <option key={`corner-${option.value}`} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </label>

            {/* Color */}
            <CustomColorPicker
                label="Color"
                color={element.color}
                onChange={(value) => { updateElement(element.id, 'color', value); }}
            />
        </div>
    );
};

ThemeSpeedLimiterIndicatorSettings.propTypes = {
    element: PropTypes.shape({
        id: PropTypes.string.isRequired,
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        size: PropTypes.number.isRequired,
        corner: PropTypes.oneOf(['top-left', 'top-right', 'bottom-left', 'bottom-right']).isRequired,
        color: PropTypes.string.isRequired,
    }).isRequired,
    updateElement: PropTypes.func.isRequired,
    openColorModal: PropTypes.func.isRequired,
};

/**
 * Speed Limiter Indicator Component
 */
const ThemeSpeedLimiterIndicator = ({
    element,
    selectedElement,
    setSelectedElement,
    updateElement,
    render, 
    editAllowed,
    scale,
    snapToGridEnabled,
    gridSize
}) => {
    const groupRef = useRef();
    const [isSettingsVisible, setIsSettingsVisible] = useState(false);
    const settingsRef = useRef(null); // Ref for settings form

    // Handle selection
    const handleSelect = (e) => {
        e.cancelBubble = true; // Prevent event from bubbling up
        setSelectedElement(element);
        setIsSettingsVisible(true);
    };

    // Handle deselection when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            // If settings are visible and the click target is not inside the settings form, deselect
            if (isSettingsVisible && settingsRef.current && !settingsRef.current.contains(e.target)) {
                setIsSettingsVisible(false);
                setSelectedElement(null);
            }
        };

        window.addEventListener('click', handleClickOutside);

        return () => window.removeEventListener('click', handleClickOutside);
    }, [isSettingsVisible, setSelectedElement]);

    // Calculate triangle points based on corner orientation
    const calculatePoints = () => {
        const size = element.size;
        switch (element.corner) {
            case '0':
                return [0, size, size, size, 0, 0];
            case '1':
                return [0, size, -size, size, 0, 0];
            case '2':
                return [0, -size, size, -size, 0, 0];
            case '3':
                return [0, -size, -size, -size, 0, 0];
            default:
                return [0, size, size, size, 0, 0];
        }
    };

    // Handle drag end
    const handleDragEnd = (e) => {
        const { x, y } = e.target.position();
        updateElement(element.id, 'x', Math.round(x));
        updateElement(element.id, 'y', Math.round(y));
    };

    // Determine if this element is selected
    const isSelected = selectedElement && selectedElement.id === element.id;

    return (
        <>
            <Group
                x={element.x}
                y={element.y}
                draggable={!render && editAllowed}
                dragBoundFunc={(pos) => { return boundDrag(pos, scale, snapToGridEnabled, gridSize, SCREEN_WIDTH, SCREEN_HEIGHT, element.size, element.size) }}
                onClick={handleSelect}
                onTap={handleSelect}
                onDragEnd={handleDragEnd}
                ref={groupRef}
                aria-label="Speed Limiter Indicator"
            >
                <Line
                    points={calculatePoints()}
                    fill={element.color}
                    closed
                    stroke={!render ? "#1976d2" : 'transparent'}
                    dash={isSelected ? [1, 1] : [0, 0]}
                    strokeWidth={0.5}
                />
            </Group>

        </>
    );
};

ThemeSpeedLimiterIndicator.propTypes = {
    element: PropTypes.shape({
        id: PropTypes.string.isRequired,
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        size: PropTypes.number.isRequired,
        corner: PropTypes.oneOf(['top-left', 'top-right', 'bottom-left', 'bottom-right']).isRequired,
        color: PropTypes.string.isRequired,
        editAllowed: PropTypes.bool,
    }).isRequired,
    selectedElement: PropTypes.object,
    setSelectedElement: PropTypes.func.isRequired,
    updateElement: PropTypes.func.isRequired,
};

export default ThemeSpeedLimiterIndicator;
